import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useInvalidateHomeState } from '../../home/hooks/use-home-state'
import { outreachApi, outreachArchiveQueryKey, outreachQueueQueryKey } from '../api'

// Approve or decline a queued contact. Approve keeps it on the board (QUEUED);
// decline archives it (drops out of the queue, appears on the Archive page), so
// we refresh both the queue and the archive on success.
export function useResolveQueuedMutation(startupId: string, action: 'approve' | 'decline') {
  const queryClient = useQueryClient()
  // Approving refills the send queue, which is what Home's drained card and the
  // sidebar's runway badge are about — without this they keep saying the
  // campaign is starving until the next full page load.
  const invalidateHome = useInvalidateHomeState(startupId)
  return useMutation({
    mutationFn: (leadId: string) =>
      action === 'approve'
        ? outreachApi.approveQueued(startupId, leadId)
        : outreachApi.declineQueued(startupId, leadId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: outreachQueueQueryKey(startupId) })
      if (action === 'decline') {
        await queryClient.invalidateQueries({ queryKey: outreachArchiveQueryKey(startupId) })
      }
      await invalidateHome()
    },
  })
}

// Approve every contact still awaiting a decision in one request. A single
// mutation (vs. firing one per lead) keeps the action atomic and avoids the
// shared-observer callback clobbering you'd hit looping `approve.mutate`.
export function useApproveAllQueuedMutation(startupId: string) {
  const queryClient = useQueryClient()
  const invalidateHome = useInvalidateHomeState(startupId)
  return useMutation({
    mutationFn: () => outreachApi.approveAllQueued(startupId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: outreachQueueQueryKey(startupId) })
      await invalidateHome()
    },
  })
}

/**
 * Skip every contact still awaiting a decision. Skipping is not rejecting: these
 * stay restorable from the Skipped tab and never count as rejection data, so
 * clearing the board this way is a legitimate choice rather than a shortcut.
 */
export function useSkipAllQueuedMutation(startupId: string) {
  const queryClient = useQueryClient()
  const invalidateHome = useInvalidateHomeState(startupId)
  return useMutation({
    mutationFn: () => outreachApi.skipAllQueued(startupId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: outreachQueueQueryKey(startupId) })
      await queryClient.invalidateQueries({ queryKey: outreachArchiveQueryKey(startupId) })
      await invalidateHome()
    },
  })
}
